import React from 'react'

export interface AvatarProps {
  name: string
  size?: 'sm' | 'md' | 'lg'
  src?: string
  'data-testid'?: string
}

// Brand-derived tint palette for avatar backgrounds with readable foreground.
// Each tint is a {bg, text} pair of Tailwind class strings.
const AVATAR_TINTS = [
  { bg: 'bg-fine-barrel', text: 'text-white' },
  { bg: 'bg-perfect-shine', text: 'text-white' },
  { bg: 'bg-emerald', text: 'text-white' },
  { bg: 'bg-anzac', text: 'text-ink' },
  { bg: 'bg-cerise-red', text: 'text-white' },
  { bg: 'bg-atlantic-mood', text: 'text-white' },
]

const sizeClasses: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'w-6 h-6 text-xs',
  md: 'w-8 h-8 text-sm',
  lg: 'w-10 h-10 text-base',
}

// Deterministic hash function: sum of char codes modulo tint list length.
const hashName = (name: string): number => {
  let sum = 0
  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i)
  }
  return sum % AVATAR_TINTS.length
}

// Extract initials: first letter of first two words, uppercased.
const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/)
  if (words.length === 0) return ''
  if (words.length === 1) return words[0][0]?.toUpperCase() ?? ''
  return (words[0][0]?.toUpperCase() ?? '') + (words[1][0]?.toUpperCase() ?? '')
}

const Avatar = ({ name, size = 'md', src, 'data-testid': testId }: AvatarProps) => {
  const initials = getInitials(name)
  const tintIndex = hashName(name)
  const tint = AVATAR_TINTS[tintIndex]
  const sizeClass = sizeClasses[size]

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        role="img"
        aria-label={name}
        className={`rounded-full object-cover ${sizeClass}`}
        data-testid={testId}
      />
    )
  }

  return (
    <div
      role="img"
      aria-label={name}
      className={`rounded-full flex items-center justify-center font-semibold ${tint.bg} ${tint.text} ${sizeClass}`}
      data-testid={testId}
    >
      {initials}
    </div>
  )
}

export default Avatar
